import { ReactElement, useEffect } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import useQueryParams from 'hooks/useQueryParams'
import useStore from 'hooks/useStore'
import { useProductUiEvent } from 'hooks'

import { isProduction } from 'helpers/environment'
import ErrorPage from 'pages/ErrorPage'

import notFoundSVG from 'assets/images/not-found.svg'
import StandardError from 'Errors/StandardError'
import serverErrorSVG from 'assets/images/server-error.svg'
import NotFoundError from 'Errors/NotFoundError'
import RetriableError from 'Errors/RetriableError'

const logError = (error: any, attribute: { [key: string]: any }): void => {
    if (isProduction()) {
        window.GrafanaFaroWebSdk?.faro.api.pushError(error, attribute)
    } else {
        console.error(error)
    }
}

const isAxiosLikeError = (
    err: unknown
): err is {
    isAxiosError: true
    code?: string
    message?: string
    response?: { status?: number }
} =>
    typeof err === 'object' &&
    err !== null &&
    (err as any).isAxiosError === true

export const getTrackedError = (
    error: unknown
): { code: string; description: string } => {
    // Mapped API errors (AccountBlockedError, DefaultError, LowGopayBalanceError, etc.)
    if (error instanceof StandardError && error.metaData) {
        return {
            code: error.metaData.errorCode ?? '',
            description: error.metaData.errorDescription ?? '',
        }
    }

    // Unmapped but retryable API errors
    if (error instanceof RetriableError) {
        return {
            code: error.code ?? '',
            description: error.message ?? error.label ?? '',
        }
    }

    // Raw axios errors that never made it through the error mapper (e.g. network errors)
    if (isAxiosLikeError(error)) {
        return {
            code: error.response?.status
                ? String(error.response.status)
                : error.code ?? '',
            description: error.message ?? '',
        }
    }

    // ServerError (StandardError with no metaData), NotFoundError, or any other native Error
    if (error instanceof Error) {
        return { code: '', description: error.message }
    }

    return { code: '', description: '' }
}

const ErrorBoundary = (props: any): ReactElement => {
    const { t } = useTranslation()
    const error = useRouteError()
    const eventManager = useProductUiEvent()
    const { reference } = useQueryParams()
    const [store] = useStore()
    const [{ paymentDetails }] = useStore()

    const instrumentType: InstrumentTypes =
        store.selectedPaymentInstructions?.[0]?.instrumentType ??
        store.paymentDetails?.payment_instructions?.[0].instrument_type ??
        'GOPAY_WALLET'
    const selectedInstallment = store.selectedInstallment

    logError(error, { reference, url: window.location.href })

    useEffect(() => {
        if (paymentDetails) {
            eventManager.trackPage('GWC_ERROR_PAGE', {
                error: getTrackedError(error),
                payment: {
                    paylaterInstallmentPlan:
                        selectedInstallment?.installment_option_id,
                    type: instrumentType,
                    referenceId: reference,
                    amount: store.paymentDetails?.total_amount.value,
                },
                merchant: {
                    id: store.paymentDetails?.merchant_x_ref_id,
                    name: store.paymentDetails?.merchant_name,
                    channelType: store.paymentDetails?.channel_type,
                },
                useTimestamp: true,
            })
        }
    }, [])

    if (
        (isRouteErrorResponse(error) && error.status === 404) ||
        error instanceof NotFoundError
    ) {
        return (
            <ErrorPage
                title={t('errors.404.title')}
                description={t('errors.404.description')}
                image={notFoundSVG}
                redirectURL={(error as any)?.redirectURL}
            />
        )
    }

    if (error instanceof StandardError) {
        return (
            <ErrorPage
                title={error.title}
                description={error.description}
                image={error.image}
                redirectURL={
                    error.redirectURL ?? store.paymentReviewData?.redirect_url
                }
                metaData={error.metaData}
                cta={error.cta}
            />
        )
    }

    return (
        <ErrorPage
            title={t('errors.500.title')}
            description={t('errors.500.description')}
            image={serverErrorSVG}
        />
    )
}

export default ErrorBoundary
